import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
        full_name: '',
        role: 'employee',
        slack_user_id: ''
    });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await api.getUsers();
            if (response.data.success) {
                setUsers(response.data.users);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedUser) {
                // Update existing user
                const updateData = { ...formData };
                // Don't send password if it's empty
                if (!updateData.password) {
                    delete updateData.password;
                }
                await api.updateUser(selectedUser.id, updateData);
            } else {
                // Create new user
                await api.register(formData);
            }
            setShowModal(false);
            resetForm();
            loadUsers();
        } catch (error) {
            alert('Error saving user: ' + (error.response?.data?.error || error.message));
        }
    };

    const resetForm = () => {
        setFormData({
            username: '',
            password: '',
            email: '',
            full_name: '',
            role: 'employee',
            slack_user_id: ''
        });
        setSelectedUser(null);
    };

    const editUser = (user) => {
        setSelectedUser(user);
        setFormData({
            username: user.username,
            email: user.email,
            full_name: user.full_name,
            role: user.role,
            slack_user_id: user.slack_user_id || '',
            password: '' // Leave empty - only fill if changing password
        });
        setShowModal(true);
    };

    const toggleUserStatus = async (id, currentStatus) => {
        try {
            await api.updateUser(id, { is_active: !currentStatus });
            loadUsers();
        } catch (error) {
            alert('Error updating user');
        }
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="page">
            <div className="page-header">
                <h1>Users</h1>
                <button onClick={() => setShowModal(true)} className="btn-primary">
                    + Add User
                </button>
            </div>

            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Slack ID</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id}>
                                <td><strong>{user.full_name}</strong></td>
                                <td>{user.username}</td>
                                <td>{user.email}</td>
                                <td>
                                    <span className={`role-badge ${user.role}`}>
                                        {user.role}
                                    </span>
                                </td>
                                <td>{user.slack_user_id || '-'}</td>
                                <td>
                                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        onClick={() => editUser(user)}
                                        className="btn-sm"
                                        style={{ marginRight: '0.5rem' }}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                                        className="btn-sm"
                                    >
                                        {user.is_active ? 'Deactivate' : 'Activate'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{selectedUser ? 'Edit User' : 'Add User'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Username *</label>
                                    <input
                                        type="text"
                                        value={formData.username}
                                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Password {selectedUser ? '(leave blank to keep current)' : '*'}</label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        required={!selectedUser}
                                        minLength="6"
                                        placeholder={selectedUser ? 'Leave blank to keep current' : ''}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Role</label>
                                    <select
                                        value={formData.role}
                                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                                    >
                                        <option value="employee">Employee</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Slack User ID</label>
                                    <input
                                        type="text"
                                        value={formData.slack_user_id}
                                        onChange={(e) => setFormData({...formData, slack_user_id: e.target.value})}
                                        placeholder="U1234567890"
                                    />
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedUser ? 'Update User' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UsersPage;